import axios from 'axios';
import type { TFunction } from 'i18next';
import type { QueryClient } from '@tanstack/react-query';

import {
  PaymentDeviceRentalRequestStatus,
  PaymentDeviceStatus,
} from '@/models/common/PaymentDeviceEnums';
import {
  PaymentDeviceDtoIn,
  PaymentDeviceRentalRequestDtoIn,
  PaymentDeviceSummaryCount,
  PaymentDeviceSummaryDtoIn,
  PendingCountDtoIn,
} from '@/models/in/PaymentDeviceDtoIn';
import { MANAGEMENT_DEVICE_FEES_URL } from './paymentDeviceFees';

/** Must match FeatureFlagKeys.DeviceRentals on the server and SeedFeatureFlag_DeviceRentals.sql. */
export const DEVICE_RENTALS_FEATURE_KEY = 'device-rentals';

export const MANAGEMENT_DEVICES_URL = 'management/payment-devices';
export const MANAGEMENT_DEVICE_REQUESTS_URL = 'management/payment-device-requests';
export const MANAGEMENT_DEVICE_REQUESTS_PENDING_COUNT_URL = `${MANAGEMENT_DEVICE_REQUESTS_URL}/pending-count`;
export const fundDevicesManagementUrl = (fundId: number) => `${MANAGEMENT_DEVICES_URL}/by-fund/${fundId}`;
export const fundDevicesUrl = (fundId: number) => `funds/${fundId}/payment-devices`;

/**
 * Every query that shows a device, a rental or a request. One action (an approval, a return) moves
 * a device, its rental and often a request at once, and they are shown on several screens - the
 * inventory, its counters, the request tabs, the badge, the fund blade - so after any write all of
 * them are refreshed together rather than each caller guessing which ones it touched.
 */
export const invalidatePaymentDeviceQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];
      return typeof key === 'string'
        && (key.startsWith(MANAGEMENT_DEVICES_URL)
          || key.startsWith(MANAGEMENT_DEVICE_REQUESTS_URL)
          || key.startsWith(MANAGEMENT_DEVICE_FEES_URL)
          || /^funds\/\d+\/payment-devices/.test(key));
    },
  });

export type PaymentDeviceAction =
  | 'edit'
  | 'assign'
  | 'handOver'
  | 'cancelAssignment'
  | 'return'
  | 'editHandOverDate'
  | 'markFaulty'
  | 'markFixed'
  | 'retire'
  | 'delete';

/**
 * The actions the office may take on a device, by its status. The server enforces the same
 * transitions (409 otherwise); this only keeps the menu from offering a dead end.
 */
export const allowedDeviceActions = (device: Pick<PaymentDeviceDtoIn, 'status' | 'hasHistory' | 'currentRental'>): PaymentDeviceAction[] => {
  const deletable = device.hasHistory ? [] : (['delete'] as PaymentDeviceAction[]);
  switch (device.status) {
    case PaymentDeviceStatus.Available:
      return ['edit', 'assign', 'markFaulty', 'retire', ...deletable];
    case PaymentDeviceStatus.Reserved:
      return device.currentRental ? ['edit', 'handOver', 'cancelAssignment'] : ['edit'];
    case PaymentDeviceStatus.Rented:
      return device.currentRental ? ['edit', 'editHandOverDate', 'return'] : ['edit'];
    case PaymentDeviceStatus.Faulty:
      return ['edit', 'markFixed', 'retire', ...deletable];
    case PaymentDeviceStatus.Retired:
      return ['edit', ...deletable];
    default:
      return [];
  }
};

/** The counters above the inventory: the active stock (retired devices are left out of the total). */
export interface PaymentDeviceCounter {
  total: number;
}

export type PaymentDeviceCounterKey = 'total' | PaymentDeviceStatus.Available | PaymentDeviceStatus.Reserved | PaymentDeviceStatus.Rented | PaymentDeviceStatus.Faulty;

export const summarizeDeviceCounts = (counts: PaymentDeviceSummaryCount[]): Record<PaymentDeviceCounterKey, PaymentDeviceCounter> => {
  const empty = (): PaymentDeviceCounter => ({ total: 0 });
  const result: Record<PaymentDeviceCounterKey, PaymentDeviceCounter> = {
    total: empty(),
    [PaymentDeviceStatus.Available]: empty(),
    [PaymentDeviceStatus.Reserved]: empty(),
    [PaymentDeviceStatus.Rented]: empty(),
    [PaymentDeviceStatus.Faulty]: empty(),
  };
  for (const { status, count } of counts) {
    if (status === PaymentDeviceStatus.Retired) continue;
    result.total.total += count;
    result[status].total += count;
  }
  return result;
};

/**
 * The «מכשירי סליקה» sidebar badge: requests waiting for approval plus devices in a continuous rental
 * of 3 months or more. Falls back to the sum when the server sends no total (an older build).
 */
export const deviceBadgeTotal = (counts: PendingCountDtoIn): number =>
  counts.total ?? counts.count + (counts.longRentalCount ?? 0);

/** The long-rental reminder pops once a day; the key is per Israel date. */
export const longRentalReminderKey = (today: string) => `paymentDevices.longRentalReminder.${today}`;

/** Today's calendar date in Israel, "YYYY-MM-DD" - the date the office stands in, whatever the browser's zone. */
export const todayInIsrael = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);

/** The calendar date of a server date ("2026-10-18T00:00:00" → "2026-10-18"). */
export const dateOnly = (value: string | null | undefined): string => (value ? value.slice(0, 10) : '');

/** "2026-10-18" → "18/10/2026", without passing through Date and its time zone. */
export const formatDeviceDate = (value: string | null | undefined): string => {
  const date = dateOnly(value);
  if (!date) return '';
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
};

const DAY_LETTERS = ["א'", "ב'", "ג'", "ד'", "ה'", "ו'"];

/**
 * "2026-11-03" → "יום ג' 03/11/2026" - the day of the week first, as the office speaks of pickup days.
 * Without the year ("יום ג' 03/11") where a table column has no room for it.
 */
export const formatPickupDay = (value: string | null | undefined, withYear = true): string => {
  const date = dateOnly(value);
  if (!date) return '';
  const [year, month, day] = date.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const shown = withYear ? formatDeviceDate(date) : formatDeviceDate(date).slice(0, 5);
  return weekday === 6 ? `שבת ${shown}` : `יום ${DAY_LETTERS[weekday]} ${shown}`;
};

/** Approved and not collected yet: a device is held for it, and which one is chosen at pickup. */
export const isAwaitingPickup = (request: Pick<PaymentDeviceRentalRequestDtoIn, 'status' | 'rental'>): boolean =>
  request.status === PaymentDeviceRentalRequestStatus.Approved && request.rental == null;

/** Free devices that no approved request is waiting for - what a new approval can still take. */
export const unheldDeviceCount = (summary: Pick<PaymentDeviceSummaryDtoIn, 'counts' | 'heldCount'>): number =>
  summary.counts.filter((c) => c.status === PaymentDeviceStatus.Available).reduce((sum, c) => sum + c.count, 0)
  - (summary.heldCount ?? 0);

export const deviceStatusColor = (status: PaymentDeviceStatus): string => {
  switch (status) {
    case PaymentDeviceStatus.Available:
      return 'var(--icon-status-6)';
    case PaymentDeviceStatus.Reserved:
      return 'var(--icon-status-2)';
    case PaymentDeviceStatus.Rented:
      return 'var(--icon-status-3)';
    case PaymentDeviceStatus.Faulty:
      return 'var(--icon-status-4)';
    default:
      return 'var(--font-tertiary)';
  }
};

export const requestStatusColor = (status: PaymentDeviceRentalRequestStatus): string => {
  switch (status) {
    case PaymentDeviceRentalRequestStatus.Pending:
      return 'var(--icon-status-1)';
    case PaymentDeviceRentalRequestStatus.Approved:
      return 'var(--icon-status-6)';
    case PaymentDeviceRentalRequestStatus.Rejected:
      return 'var(--icon-status-4)';
    default:
      return 'var(--font-tertiary)';
  }
};

/**
 * The server answers a refused action with a plain Hebrew sentence (BadRequest/Conflict/NotFound
 * with a string body) that says exactly what is wrong - "the device was already assigned" - so it
 * is shown as is. Anything else falls back to the generic message for the action.
 */
export const paymentDeviceErrorMessage = (err: unknown, t: TFunction, fallbackKey: string): string => {
  if (axios.isAxiosError(err) && typeof err.response?.data === 'string' && err.response.data.trim()) {
    return err.response.data;
  }
  return t(fallbackKey);
};
